// A toy thrown at the screen: it flies toward the camera, flattens against an
// invisible pane of glass, sticks, slides down, peels away and comes back.
// The soft body is carried kinematically (rest, current and previous positions
// move together, so the carry adds no velocity) and glass contact is a
// unilateral position projection after each XPBD substep, as in position-based
// contact handling (Müller et al. 2007/2020). Stuck nodes keep their contact
// point until a peel front passes them. All timings are art direction.
const clamp=(v,a,b)=>Math.max(a,Math.min(b,v));
const smooth=t=>{t=clamp(t,0,1);return t*t*(3-2*t);};
export const SPLAT_PHASES=['idle','flying','stuck','sliding','peeling','returning'];

export class SplatThrow{
  constructor(body,{flight=.3,stick=1.1,slide=1.3,peel=.55,squash=.7,slideDistance=.32,gap=.004,press=40}={}){
    Object.assign(this,{body,flight,stick,slide,peel,squash,slideDistance,gap,press});
    this.count=body.count;
    this.stuck=new Uint8Array(this.count);this.stickPoint=new Float64Array(this.count*3);
    this.offset=[0,0,0];this.applied=[0,0,0];this.phase='idle';this.time=0;
    this.impacts=0;this.onImpact=null;this.patch=null;this.peelLevel=Infinity;
  }
  get active(){return this.phase!=='idle';}
  // plane: {point, toward} in toy-local space; toward is a unit vector from the toy toward the viewer.
  launch({point,toward,lateral=[0,0,0]}){
    if(!point?.every(Number.isFinite)||!toward?.every(Number.isFinite))return false;
    const length=Math.hypot(...toward);if(length<1e-6)return false;
    this.toward=toward.map(v=>v/length);this.point=[...point];
    // How far the rest shape must travel so its front passes the glass by squash x depth.
    const b=this.body,r=b.rest;let front=-Infinity,back=Infinity;
    for(let i=0;i<b.count;i++){const s=this.signed(r,i);front=Math.max(front,s);back=Math.min(back,s);}
    const travel=-front+this.squash*(front-back);
    const lat=lateral.map((v,a)=>v-this.toward[a]*lateral.reduce((s,n,k)=>s+n*this.toward[k],0));
    this.target=this.toward.map((v,a)=>v*travel+lat[a]);
    this.start=[...this.applied];this.time=0;this.phase='flying';this.stuck.fill(0);this.patch=null;this.peelLevel=Infinity;this.hit=false;
    b.substepHook=(p,prev,rest,h)=>this.substep(p,prev,rest,h);b.activity=Math.max(b.activity,1);
    // Press harder toward the rest shape beyond the glass, so the jelly really flattens.
    this.savedRecovery=b.recovery;b.recovery=new Float64Array(b.count).fill(this.press);
    return true;
  }
  signed(array,i){const k=i*3,t=this.toward,q=this.point;return (array[k]-q[0])*t[0]+(array[k+1]-q[1])*t[1]+(array[k+2]-q[2])*t[2];}
  restoreRecovery(){if(this.savedRecovery!==undefined){this.body.recovery=this.savedRecovery;this.savedRecovery=undefined;}}
  cancel(){
    this.restoreRecovery();
    this.phase='idle';this.time=0;this.offset=[0,0,0];this.applied=[0,0,0];this.stuck.fill(0);this.patch=null;
    if(this.body.substepHook)this.body.substepHook=null;
  }
  update(dt){
    if(!Number.isFinite(dt)||dt<=0||this.phase==='idle')return;
    this.time+=dt;
    const down=[0,-1,0],t=this.toward,along=down[1]*t[1];
    const slideDir=[-t[0]*along,-1-t[1]*along,-t[2]*along],slideLength=Math.hypot(...slideDir)||1;
    if(this.phase==='flying'){
      // Accelerating approach reads as coming at the viewer.
      const u=clamp(this.time/this.flight,0,1),e=u*u;
      this.offset=this.start.map((v,a)=>v+(this.target[a]-v)*e);
      if(u>=1){this.phase='stuck';this.time=0;}
    }else if(this.phase==='stuck'){
      if(this.time>=this.stick){this.phase='sliding';this.time=0;this.slideFrom=[...this.offset];this.measurePatch();}
    }else if(this.phase==='sliding'){
      const u=smooth(this.time/this.slide);
      this.offset=this.slideFrom.map((v,a)=>v+slideDir[a]/slideLength*this.slideDistance*u);
      // The sticky contact creeps after the body; the peel front moves from top to bottom.
      const creep=this.slideDistance*.55*u;
      for(let i=0;i<this.count;i++)if(this.stuck[i]===1){
        this.stickPoint[i*3+1]=this.stickBase[i]+slideDir[1]/slideLength*creep;
      }
      if(this.patch)this.peelLevel=this.patch.maxY-(this.patch.maxY-this.patch.minY)*u*1.1;
      if(this.time>=this.slide){this.phase='peeling';this.time=0;this.peelFrom=[...this.offset];this.restoreRecovery();}
    }else if(this.phase==='peeling'){
      this.peelLevel=-Infinity;
      const u=smooth(this.time/this.peel);
      this.offset=this.peelFrom.map((v,a)=>v*(1-u));
      if(u>=1){this.phase='returning';this.time=0;this.offset=[0,0,0];}
    }else if(this.phase==='returning'){
      // Keep the hook until the carry is fully undone, then let the jelly wobble home.
      if(this.applied.every(v=>Math.abs(v)<1e-9)){this.cancel();}
    }
  }
  measurePatch(){
    const b=this.body,p=b.p;let minY=Infinity,maxY=-Infinity,count=0;
    this.stickBase=new Float64Array(this.count);
    for(let i=0;i<this.count;i++)if(this.stuck[i]){count++;minY=Math.min(minY,p[i*3+1]);maxY=Math.max(maxY,p[i*3+1]);this.stickBase[i]=this.stickPoint[i*3+1];}
    this.patch=count?{minY,maxY,count}:null;
  }
  substep(p,prev,rest,h){
    // Kinematic carry: move the whole frame without adding velocity.
    const d=this.offset.map((v,a)=>v-this.applied[a]);
    if(d[0]||d[1]||d[2]){
      for(let i=0;i<p.length;i+=3)for(let a=0;a<3;a++){p[i+a]+=d[a];prev[i+a]+=d[a];rest[i+a]+=d[a];}
      this.applied=[...this.offset];
    }
    if(this.phase==='idle'||this.phase==='returning')return;
    const t=this.toward,stickyPhase=this.phase==='stuck'||this.phase==='sliding';
    for(let i=0;i<this.count;i++){
      const k=i*3,s=this.signed(p,i);
      if(this.stuck[i]===1&&stickyPhase&&p[k+1]<=this.peelLevel){
        // Adhesion: a stuck node keeps its contact point on the glass.
        p[k]=this.stickPoint[k];p[k+1]=this.stickPoint[k+1];p[k+2]=this.stickPoint[k+2];prev[k]=p[k];prev[k+1]=p[k+1];prev[k+2]=p[k+2];
        continue;
      }
      if(s>-this.gap){
        const push=s+this.gap;p[k]-=t[0]*push;p[k+1]-=t[1]*push;p[k+2]-=t[2]*push;
        // Inelastic contact: drop only the normal velocity, so the body spreads
        // sideways instead of bouncing off the glass.
        const back=this.signed(prev,i)-(s-push);
        if(back>0){prev[k]-=t[0]*back;prev[k+1]-=t[1]*back;prev[k+2]-=t[2]*back;}
        if(!this.hit){this.hit=true;this.impacts++;this.onImpact?.();}
        if(stickyPhase&&this.stuck[i]===0&&p[k+1]>-Infinity){this.stuck[i]=1;this.stickPoint[k]=p[k];this.stickPoint[k+1]=p[k+1];this.stickPoint[k+2]=p[k+2];}
        else if(this.stuck[i]===1&&p[k+1]>this.peelLevel)this.stuck[i]=2;
      }
    }
  }
}
